import {ProviderLogo} from '@github-ui/copilot-byok/providers/ProviderLogo'
import {AUTO_MODEL_ID} from '@github-ui/copilot-chat-helpers/auto-model'
import {BASE_MODEL_ID, isCustomModel} from '@github-ui/copilot-chat-state/models'
import {type CopilotChatModel, CopilotPlan} from '@github-ui/copilot-chat-types'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {Tooltip} from '@github-ui/pacer/Tooltip'
import {CopilotIcon} from '@primer/octicons-react'
import {ActionList, Label, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import React, {useRef, useState} from 'react'

import {useActiveModelPromo} from '../hooks/use-active-model-promo'
import {getMinimumRequiredPlan} from '../utils/model-picker-helpers'
import {ModelInfoPopover} from './ModelInfoPopover'
import styles from './ModelPicker.module.css'

/**
 * Formats a billing multiplier for display.
 * @param multiplier - The billing multiplier value
 * @returns A formatted string like "3x", "1.5x", "0.33x", or "0x" (default)
 */
function formatMultiplier(multiplier: number | undefined): string {
  // Default to 0 if no multiplier is defined
  const value = multiplier ?? 0

  // Convert to number to remove trailing zeros, then back to string
  return `${Number(value.toFixed(2))}x`
}

interface ModelListItemProps {
  model: CopilotChatModel
  selected: boolean
  onModelPicked: (m: CopilotChatModel) => void
}

export const ModelListItem = React.memo(function ModelListItemComponent({
  model,
  selected,
  onModelPicked,
  isTokenBasedBillingEnabled = false,
  autoPinnedModelDisplayName,
  autoPinnedModel,
  autoPinnedModelDiscount,
  autoModeDiscounts,
}: ModelListItemProps & {
  isTokenBasedBillingEnabled?: boolean
  autoPinnedModelDisplayName?: string
  autoPinnedModel?: CopilotChatModel
  autoPinnedModelDiscount?: number
  autoModeDiscounts?: Record<string, number>
}) {
  const [isInfoOpen, setIsInfoOpen] = useState(false)
  const {plan, premiumInteractionsQuotaExceeded, chatQuotaExceeded, overagesEnabled, canUpgradePlan} = useEntitlement()
  const itemRef = useRef<HTMLLIElement>(null)
  const freePlanAndOutOfQuota = plan === CopilotPlan.IndividualFree && chatQuotaExceeded
  const premiumModelAndOutOfQuota =
    model.billing?.is_premium &&
    plan !== CopilotPlan.IndividualFree &&
    premiumInteractionsQuotaExceeded &&
    !overagesEnabled
  const modelOutOfPlan = plan && model.billing?.restricted_to && !model.billing?.restricted_to.includes(plan)
  const isUpsellModel = model.is_upsell === true
  const minRequiredPlan = getMinimumRequiredPlan(model)
  const isProPlusModel = minRequiredPlan === CopilotPlan.IndividualProPlus
  const isMaxOnlyModel = minRequiredPlan === CopilotPlan.IndividualMax && copilotFeatureFlags.maxUpsellEnabled
  const locked = freePlanAndOutOfQuota || premiumModelAndOutOfQuota || modelOutOfPlan || isUpsellModel
  const activePromo = useActiveModelPromo(copilotFeatureFlags.modelPickerPromotions ? model.billing?.promo : undefined)
  const visiblePromo = locked ? undefined : activePromo

  const lockedTooltipText = locked
    ? getLockedModelTooltip({
        isUpsellOrOutOfPlan: !!(isUpsellModel || modelOutOfPlan),
        freePlanAndOutOfQuota: !!freePlanAndOutOfQuota,
        premiumModelAndOutOfQuota: !!premiumModelAndOutOfQuota,
        isProPlusOnly: isProPlusModel,
        isMaxOnly: isMaxOnlyModel,
        canUpgradePlan,
      })
    : null

  // Render Multiplier when in Premium Request Unit (Auto pseudo-model has no real billing multiplier)
  const showMultiplierIndicators =
    model.id !== AUTO_MODEL_ID && !isCustomModel(model) && !isTokenBasedBillingEnabled && !isUpsellModel && !locked
  const multiplierLabelText = showMultiplierIndicators ? formatMultiplier(model.billing?.multiplier) : null
  const promoDiscountPercent = visiblePromo?.discount_percent
  const roundedPromoDiscountPercent =
    promoDiscountPercent !== undefined && Number.isFinite(promoDiscountPercent)
      ? Number(promoDiscountPercent.toFixed(2))
      : null
  const promoDiscountLabel =
    roundedPromoDiscountPercent !== null &&
    roundedPromoDiscountPercent > 0 &&
    promoDiscountPercent !== undefined &&
    promoDiscountPercent <= 100
      ? `${roundedPromoDiscountPercent}% off`
      : null

  // The model's info popover should be available only if the model has billable content to show (token prices or Auto's pinned model) and isn't locked
  const hasInfoContent = isTokenBasedBillingEnabled && (!!model.billing?.token_prices || model.id === AUTO_MODEL_ID)

  // On the Auto row, reveal the thread's pinned model on hover/focus with descriptive
  // tooltip copy that also serves as the row's accessible description. When the rich info
  // popover is active for the Auto row (token-based billing + billable info content), it already surfaces the
  // pinned model, so suppress this plain tooltip to avoid a double hover. The Auto row can
  // itself be locked (e.g. a free-plan user out of chat quota), in which case
  // lockedTooltipText takes priority — the quota message matters more than the pin reveal.
  const pinnedModelRowTooltip =
    model.id === AUTO_MODEL_ID && autoPinnedModelDisplayName && !hasInfoContent
      ? `Auto is currently using ${autoPinnedModelDisplayName}`
      : null
  // Bake the "Model locked." framing into the tooltip text so it doubles as the row's
  // accessible description: Pacer wires the tooltip text via aria-describedby, which wins
  // over a separate aria-description string. Locked takes priority over the pin reveal.
  const lockedRowTooltip = lockedTooltipText ? `Model locked. ${lockedTooltipText}` : null
  const baseRowTooltip = lockedRowTooltip ?? pinnedModelRowTooltip
  const rowTooltipText =
    baseRowTooltip && visiblePromo?.message ? `${baseRowTooltip}. ${visiblePromo.message}` : baseRowTooltip
  const promoDescriptionId = React.useId()
  const infoDescriptionId = isInfoOpen && hasInfoContent && !lockedTooltipText ? `model-info-${model.id}` : undefined
  const itemDescriptionIds =
    [visiblePromo?.message ? promoDescriptionId : undefined, infoDescriptionId].filter(Boolean).join(' ') || undefined

  const item = (
    <ActionList.Item
      key={model.id}
      onSelect={() => onModelPicked(model)}
      selected={selected}
      disabled={!!locked}
      aria-description={lockedRowTooltip ?? undefined}
      aria-describedby={itemDescriptionIds}
      ref={itemRef}
      onMouseEnter={() => {
        if (hasInfoContent && !locked) setIsInfoOpen(true)
      }}
      onMouseLeave={e => {
        // Keep open if the mouse moved into the popover. relatedTarget is the
        // overlay container (an ancestor of the content div), so we must check
        // the AnchoredOverlay root rather than the content div itself.
        const popoverEl = document.getElementById(`model-info-${model.id}`)
        const overlayContainer = popoverEl?.closest('[data-component="AnchoredOverlay"]')
        if (overlayContainer?.contains(e.relatedTarget as Node)) return
        setIsInfoOpen(false)
      }}
      onFocus={e => {
        // Only show the popover when navigating between list items with the
        // keyboard (relatedTarget is another <li>). This excludes the
        // automatic focus Primer's FocusTrap applies when the menu opens,
        // which has no <li> predecessor.
        if (!(e.relatedTarget instanceof HTMLLIElement)) return
        if (hasInfoContent && !locked) setIsInfoOpen(true)
      }}
      onBlur={() => setIsInfoOpen(false)}
    >
      <ActionList.LeadingVisual>
        <ModelVendorIcon model={model} />
      </ActionList.LeadingVisual>
      <span className={clsx(locked && styles.modelNameDisabled)}>
        {model.displayName}
        {model.vendor && <VisuallyHidden>, {model.vendor} provider</VisuallyHidden>}
      </span>
      {locked && isMaxOnlyModel && <span className={styles.maxBadge}>Max</span>}
      {locked && isProPlusModel && <span className={styles.proPlusBadge}>Pro+</span>}
      {copilotFeatureFlags.premiumRequestQuotasEnabled && model.preview && model.id !== BASE_MODEL_ID && (
        <span className={styles.modelMetaLabel}>Preview</span>
      )}
      {visiblePromo?.message && (
        <ActionList.Description variant="block">
          <span id={promoDescriptionId}>{visiblePromo.message}</span>
        </ActionList.Description>
      )}
      {promoDiscountLabel ? (
        <ActionList.TrailingVisual>
          <Label variant="accent">{promoDiscountLabel}</Label>
        </ActionList.TrailingVisual>
      ) : (
        showMultiplierIndicators && (
          <ActionList.TrailingVisual>
            <span className={styles.multiplier}>{multiplierLabelText}</span>
          </ActionList.TrailingVisual>
        )
      )}
    </ActionList.Item>
  )

  const infoPopover =
    hasInfoContent && !locked ? (
      <ModelInfoPopover
        model={model}
        autoPinnedModel={autoPinnedModel}
        autoPinnedModelDisplayName={model.id === AUTO_MODEL_ID ? autoPinnedModelDisplayName : undefined}
        autoPinnedModelDiscount={autoPinnedModelDiscount}
        autoModeDiscounts={model.id === AUTO_MODEL_ID ? autoModeDiscounts : undefined}
        anchorRef={itemRef}
        isOpen={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
      />
    ) : null

  if (rowTooltipText) {
    return (
      <>
        <Tooltip text={rowTooltipText} position="top">
          {item}
        </Tooltip>
        {infoPopover}
      </>
    )
  }
  return (
    <>
      {item}
      {infoPopover}
    </>
  )
})

function getLockedModelTooltip({
  isUpsellOrOutOfPlan,
  freePlanAndOutOfQuota,
  premiumModelAndOutOfQuota,
  isProPlusOnly,
  isMaxOnly,
  canUpgradePlan,
}: {
  isUpsellOrOutOfPlan: boolean
  freePlanAndOutOfQuota: boolean
  premiumModelAndOutOfQuota: boolean
  isProPlusOnly: boolean
  isMaxOnly: boolean
  canUpgradePlan: boolean
}): string {
  if (freePlanAndOutOfQuota) return "You've used all free requests this month."
  if (premiumModelAndOutOfQuota) return "You've reached your premium request limit this month."
  const planName = isMaxOnly
    ? 'Copilot Max'
    : isProPlusOnly
      ? 'Copilot Pro+'
      : isUpsellOrOutOfPlan
        ? 'Copilot Pro'
        : null
  if (!planName) return 'This model is not available on your plan.'
  return canUpgradePlan ? `Upgrade to ${planName} to access this model` : `This model requires ${planName}`
}

// `decorative` renders the icon without an accessible name (empty alt). Use it where the
// model is already named by adjacent visible/sr-only text — e.g. the trigger button — so
// the vendor logo isn't announced redundantly. Octicons are always aria-hidden regardless.
export function ModelVendorIcon({model, decorative = false}: {model: CopilotChatModel; decorative?: boolean}) {
  // Auto is the GitHub Copilot pseudo-model; always show the Copilot mark even if a
  // CAPI-sourced Auto entry arrives carrying a vendor logoURL.
  if (model.id === AUTO_MODEL_ID) {
    return <CopilotIcon />
  }

  if (isCustomModel(model) && model.custom_model.provider) {
    return <ProviderLogo provider={model.custom_model.provider} />
  }

  return model.logoURL ? (
    <GitHubAvatar alt={decorative ? '' : `${model.vendor} logo`} square size={18} src={model.logoURL} />
  ) : (
    <CopilotIcon />
  )
}
